import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { TableVirtuoso } from 'react-virtuoso';
import { useChain } from '../api';
import { useHftStore } from '../store';
import type { ChainRow } from '../types';

const UNDERLYINGS = ['NIFTY', 'BANKNIFTY', 'MIDCPNIFTY'];

/** Map IV (0..1) to a color interpolation between blue (low) and red (high). */
function ivColor(iv: number | null): string {
  if (iv == null || isNaN(iv)) return 'transparent';
  const clamped = Math.max(0, Math.min(iv, 1));
  const r = Math.round(clamped * 220);
  const b = Math.round((1 - clamped) * 220);
  return `rgba(${r}, 40, ${b}, 0.2)`;
}

function fmtN(n: number | null, decimals = 2): string {
  if (n == null || isNaN(n)) return '-';
  return n.toFixed(decimals);
}

function fmtInt(n: number | null): string {
  if (n == null) return '-';
  return n.toLocaleString();
}

function defaultDate(): string {
  // Default to most recent weekday.
  const d = new Date();
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function OptionsChainView() {
  const { current } = useHftStore();
  const [date, setDate] = useState(current.nav.date || defaultDate());
  const [underlying, setUnderlying] = useState(current.nav.underlying || 'NIFTY');
  const [selectedExpiry, setSelectedExpiry] = useState<string | null>(null);

  const { data, isLoading } = useChain(underlying, date);

  // Filter by expiry.
  const activeExpiry = selectedExpiry || data?.expiries?.[0] || null;
  const filtered = useMemo(() => {
    if (!data?.rows || !activeExpiry) return [];
    return data.rows.filter((r) => r.expiry === activeExpiry);
  }, [data, activeExpiry]);

  // Build strike-centric rows: { strike, ce?, pe? }
  const strikeRows = useMemo(() => {
    const map = new Map<number, { ce?: ChainRow; pe?: ChainRow }>();
    for (const r of filtered) {
      if (!map.has(r.strike)) map.set(r.strike, {});
      const entry = map.get(r.strike)!;
      if (r.type === 'CE') entry.ce = r;
      else entry.pe = r;
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([strike, { ce, pe }]) => ({ strike, ce, pe }));
  }, [filtered]);

  const atmStrike = data?.spot
    ? strikeRows.reduce(
        (best, r) => (Math.abs(r.strike - data.spot!) < Math.abs(best - data.spot!) ? r.strike : best),
        strikeRows[0]?.strike ?? 0,
      )
    : null;

  if (isLoading) return <LinearProgress sx={{ borderRadius: 1 }} />;

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label="Date"
          size="small"
          value={date}
          onChange={(e) => { setDate(e.target.value); setSelectedExpiry(null); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Underlying</InputLabel>
          <Select value={underlying} label="Underlying" onChange={(e) => setUnderlying(e.target.value)}>
            {UNDERLYINGS.map((u) => (
              <MenuItem key={u} value={u}>{u}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {data && (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {data.expiries.map((exp) => (
              <Chip
                key={exp}
                label={exp}
                size="small"
                color={exp === activeExpiry ? 'primary' : 'default'}
                variant={exp === activeExpiry ? 'filled' : 'outlined'}
                onClick={() => setSelectedExpiry(exp)}
                sx={{ fontSize: 11 }}
              />
            ))}
          </Box>
        )}

        {data?.spot && (
          <Typography variant="body2" fontWeight={600} ml="auto">
            Spot: {data.spot.toFixed(2)}
          </Typography>
        )}
      </Box>

      {strikeRows.length === 0 ? (
        <Typography color="text.secondary">No chain data for this date.</Typography>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <TableVirtuoso
            style={{ height: 'calc(100vh - 340px)', minHeight: 400 }}
            data={strikeRows}
            fixedHeaderContent={() => (
              <tr style={{ background: 'rgba(30,41,59,0.85)' }}>
                {/* CE side */}
                <th style={hdr}>IV</th>
                <th style={hdr}>Delta</th>
                <th style={{ ...hdr, textAlign: 'right' }}>LTP</th>
                <th style={{ ...hdr, textAlign: 'right' }}>OI</th>
                <th style={{ ...hdr, textAlign: 'right' }}>Vol</th>
                {/* Strike */}
                <th style={{ ...hdr, textAlign: 'center', fontWeight: 800, fontSize: 12, background: 'rgba(59,130,246,0.15)' }}>
                  Strike
                </th>
                {/* PE side */}
                <th style={hdr}>Vol</th>
                <th style={hdr}>OI</th>
                <th style={hdr}>LTP</th>
                <th style={hdr}>Delta</th>
                <th style={hdr}>IV</th>
              </tr>
            )}
            itemContent={(_, row) => {
              const isATM = row.strike === atmStrike;
              const rowBg = isATM ? 'rgba(59,130,246,0.08)' : 'transparent';
              return (
                <>
                  {/* CE */}
                  <td style={{ ...cell, background: ivColor(row.ce?.iv ?? null) }}>{fmtN(row.ce?.iv, 4)}</td>
                  <td style={cell}>{fmtN(row.ce?.delta, 4)}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{fmtN(row.ce?.ltp)}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{fmtInt(row.ce?.oi ?? null)}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{fmtInt(row.ce?.vol ?? null)}</td>
                  {/* Strike */}
                  <td
                    style={{
                      ...cell,
                      textAlign: 'center',
                      fontWeight: 800,
                      fontFamily: 'monospace',
                      background: isATM ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.04)',
                      borderLeft: '2px solid rgba(59,130,246,0.3)',
                      borderRight: '2px solid rgba(59,130,246,0.3)',
                    }}
                  >
                    {row.strike}
                  </td>
                  {/* PE */}
                  <td style={{ ...cell }}>{fmtInt(row.pe?.vol ?? null)}</td>
                  <td style={{ ...cell }}>{fmtInt(row.pe?.oi ?? null)}</td>
                  <td style={{ ...cell, fontWeight: 600 }}>{fmtN(row.pe?.ltp)}</td>
                  <td style={cell}>{fmtN(row.pe?.delta, 4)}</td>
                  <td style={{ ...cell, background: ivColor(row.pe?.iv ?? null) }}>{fmtN(row.pe?.iv, 4)}</td>
                </>
              );
            }}
          />
        </Paper>
      )}
    </Box>
  );
}

const hdr: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#94a3b8',
  borderBottom: '1px solid rgba(148,163,184,0.2)',
  whiteSpace: 'nowrap',
};

const cell: React.CSSProperties = {
  padding: '3px 8px',
  fontSize: 12,
  fontFamily: 'monospace',
  borderBottom: '1px solid rgba(148,163,184,0.06)',
  whiteSpace: 'nowrap',
};
